import React, { useState, useContext, useEffect, useMemo } from 'react';
import { addressesPathTo } from '../utils/Api';
import { UserContext } from '../contexts/UserContext';
import { ShoppingCartProductsContext } from '../contexts/ShoppingCartProductsContext';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Button, TextField, Typography, Checkbox } from '@mui/material';
import SnackbarContext from '../contexts/SnackbarContext';
import axios from 'axios';

/* Icons */
import CloseIcon from '@mui/icons-material/Close';
import FormControlLabel from '@mui/material/FormControlLabel';
import { layoutButtonPillRadius, layoutCardRadius, layoutIconButtonRadius, layoutShopSheetRadius } from '../theme/colors';

/* Padrão Campos Endereço */
const FormAddress = ({ label, children, sx = {} }) => (

  /* Info Endereço */
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, alignSelf: 'stretch', ...sx }}>

    {/* Título Campo */}
    <Typography sx={{ color: '#000', fontFamily: 'Nunito', fontSize: '14px', fontWeight: 700, lineHeight: '14px' }}>
      {label}
    </Typography>

    {/* TextField // Autocomplete*/}
    {children}
  </Box>
);

const AddressForm = () => {
  const { currentUser, setCurrentUser } = useContext(UserContext);
  const { shoppingCartProducts } = useContext(ShoppingCartProductsContext);
  const { openSnackbar } = useContext(SnackbarContext);
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const addressId = params.id;
  const editMode = useMemo(() => Boolean(addressId), [addressId]);
  const [cep, setCep] = useState('');
  const [address, setAddress] = useState({
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    localidade: '',
    uf: '',
  });
  const [rememberAddress, setRememberAddress] = useState(false);

  useEffect(() => {
    document.title = 'Endereço - TudoPets.app';
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!currentUser) return;

      if (editMode && addressId) {
        try {
          const result = await addressesPathTo.get(addressId);
          const data = result.data;
          setAddress(data);
          setCep(data?.cep || '');
          setRememberAddress(Boolean(data?.is_default));
        } catch (e) {
          openSnackbar('Não foi possível carregar o endereço', 'error');
          navigate('/addresses', { state: location.state });
        }
        return;
      }

      // new address
      setAddress({
        cep: '',
        logradouro: '',
        numero: '',
        complemento: '',
        bairro: '',
        localidade: '',
        uf: '',
      });
      setCep('');
      setRememberAddress(Boolean(currentUser?.default_address?.id) ? false : true);
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressId, editMode, currentUser?.id]);

  // Função para cancelar
  const handleCancel = () => {
    openSnackbar("Alterações canceladas", "info");
    navigate('/addresses', { state: location.state });
  };

  const handleBuscaApiCall = async (event) => {
    const { value } = event.target;
    const cepLimpo = value.replace(/\D/g, '');
    setCep(cepLimpo || '');
    setAddress((prev) => ({ ...prev, cep: cepLimpo || '' }));
    if (cepLimpo.length === 8) {
      try {
        const response = await axios.get('https://viacep.com.br/ws/' + cepLimpo + '/json/');
        setAddress((prev) => ({ ...prev, ...response.data, cep: cepLimpo }));
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleAddressChange = (event) => {
    event.preventDefault();
    const { name, value } = event.target;
    setAddress({ ...address, [name]: value });
  }

  const handleSubmit = (event) => {
    event.preventDefault();
    const curr = JSON.parse(localStorage.getItem('current_user'));
    const data = { ...address, user_id: currentUser.id, is_default: rememberAddress };

    const afterSave = (saved) => {
      if (!curr) return;

      if (saved?.is_default) {
        const updated = { ...curr, default_address: saved };
        localStorage.setItem('current_user', JSON.stringify(updated));
        setCurrentUser(updated);
      }

      openSnackbar(editMode ? "Endereço atualizado com sucesso!" : "Endereço salvo com sucesso!", "success");

      const fromCheckout = new URLSearchParams(location.search).get('from') === 'checkout';
      if (fromCheckout) {
        navigate('/shopping-cart');
      } else {
        navigate('/addresses', { state: location.state });
      }
    };

    if (editMode && addressId) {
      addressesPathTo.update(addressId, data).then((result) => afterSave(result.data));
      return;
    }

    addressesPathTo.save(data).then((result) => afterSave(result.data));
  };

  const buttonStyle = {
    display: 'flex',
    width: '100%',
    p: '16px 0',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
    borderRadius: layoutButtonPillRadius,
    fontFamily: 'Nunito',
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '24px'
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '24px', gap: 3, flexShrink: 0, bgcolor: 'surface.card', borderRadius: layoutShopSheetRadius }}>
      {/* Título e Close */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, alignSelf: 'stretch', justifyContent: 'space-between' }}>
        <Typography variant="pageTitle" textAlign="left" sx={{ width: '100%', m: 0, color: '#263238' }}>
          {editMode ? 'Editar endereço' : 'Adicionar endereço'}
        </Typography>

        {/* Botão para Voltar */}
        <Button data-no-shadow="true" onClick={() => window.history.back()} sx={{ borderRadius: layoutIconButtonRadius }}>
          <CloseIcon sx={{ color: '#000' }} />
        </Button>
      </Box>

      {/* Formulário */}
      <FormAddress label="CEP">
        <TextField
          name="cep"
          placeholder="ex.: 80010000"
          variant="outlined"
          type="text"
          value={cep}
          onChange={handleBuscaApiCall}
          fullWidth
          sx={{ bgcolor: 'surface.input', borderRadius: layoutCardRadius }} />
      </FormAddress>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', alignSelf: 'stretch' }}>
        <FormAddress label="Cidade" sx={{ flex: 1 }}>
          <TextField
            name="localidade"
            //label="Cidade"
            variant="outlined"
            type="text"
            value={address.localidade}
            disabled
            InputProps={{ readOnly: true }}
            fullWidth
            sx={{ bgcolor: 'surface.input', borderRadius: layoutCardRadius }} />
        </FormAddress>

        <FormAddress label="Estado" sx={{ flex: 1 }}>
          <TextField
            name="uf"
            //label="Estado"
            variant="outlined"
            type="text"
            value={address.uf}
            disabled
            InputProps={{ readOnly: true }}
            fullWidth
            sx={{ bgcolor: 'surface.input', borderRadius: layoutCardRadius }} />
        </FormAddress>
      </Box>

      <FormAddress label="Rua">
        <TextField
          name="logradouro"
          //label="Rua"
          variant="outlined"
          type="text"
          value={address.logradouro}
          disabled
          InputProps={{ readOnly: true }}
          fullWidth
          sx={{ bgcolor: 'surface.input', borderRadius: layoutCardRadius }} /></FormAddress>

      <FormAddress label="Bairro">
        <TextField
          name="bairro"
          //label="Bairro"
          variant="outlined"
          type="text"
          value={address.bairro}
          disabled
          InputProps={{ readOnly: true }}
          fullWidth
          sx={{ bgcolor: 'surface.input', borderRadius: layoutCardRadius }} /></FormAddress>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', alignSelf: 'stretch' }}>
        <FormAddress label="Número" sx={{ flex: 1 }}>
          <TextField
            name="numero"
            //label="Número"
            onChange={handleAddressChange}
            variant="outlined"
            type="text"
            value={address.numero}
            fullWidth
            sx={{ bgcolor: 'surface.input', borderRadius: layoutCardRadius }} />
        </FormAddress>

        <FormAddress label="Complemento" sx={{ flex: 1 }}>
          <TextField
            name="complemento"
            placeholder="ex.: Apto 12, Bloco B, Casa 3"
            onChange={handleAddressChange}
            variant="outlined"
            type="text"
            value={address.complemento}
            fullWidth
            sx={{ bgcolor: 'surface.input', borderRadius: layoutCardRadius }} />
        </FormAddress>
      </Box>

      <FormAddress label="Nota para o entregador">
        <TextField
          placeholder="Instruções para entrega (opcional)"
          variant="outlined"
          name="description"
          fullWidth
          autoComplete="off"
          multiline  // Para permitir múltiplas linhas
          rows={3}   // Número aproximado de linhas para 98px
          sx={{ bgcolor: 'surface.input', borderRadius: layoutCardRadius }} />
      </FormAddress>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, alignSelf: 'stretch' }}>
        {/* Checkbox para relembrar endereço */}
        <FormControlLabel
          sx={{ gap: 1 }}
          control={
            <Checkbox
              data-no-shadow="true"
              checked={rememberAddress}
              onChange={(e) => setRememberAddress(e.target.checked)}
            />
          }
          label={
            <Typography sx={{ fontFamily: 'Nunito', fontSize: '16px', fontWeight: 400, lineHeight: '24px' }}>
              Deseja salvar como endereço principal?
            </Typography>
          }
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', alignSelf: 'stretch' }}>
        <Button color="error" variant="contained" type="submit" onClick={handleSubmit}
          sx={{ ...buttonStyle }}
        >
          {editMode ? 'Salvar' : 'Adicionar endereço'}
        </Button>
        <Button color="error" variant="contained" onClick={handleCancel}
          sx={{ ...buttonStyle, bgcolor: '#35454D' }}
        >
          Cancelar
        </Button>
      </Box>
    </Box>
  );
};

export default AddressForm;
